// FTPIG / UHC / Golden Rule Underwriting Data — carrier-accurate.
//
// Sourced from the actual UHC / Golden Rule supplemental brochures
// the operator shared:
//   • Short Term Medical (STM)            cert GRI-STAG20-C-E-D + state variations
//   • AccidentWise                        policy AI-GRI + state vars  (50341-G-0325)
//   • Accident ExpenseGuard               policy APG-GRI-*            (45395B-G-1023)
//   • Accident ProGap                     policy APG-GRI-*            (45396B-G-1023)
//   • Accident ProGuard / ProGuard GI     policy APG-GRI-*            (45397B-G-1023)
//   • AdvantageGuard (Hospital Indemnity) policy HI-GRI + state vars  (49835-G-0426)
//   • CriticalGuard (Critical Illness)    policy CI-*                 (49836-G-0226)
//   • Dental Savings Complete             discount program (51158-DB-0225) — NOT INSURANCE
//   • DentalWise                          policy DEN-CH-GRI           (50337-G-1224)
//   • Hospital SafeGuard GI               policy GIFI-GRI             (47684-G-0226)
//   • HospitalWise                        policy HW-GRI               (50841-G-0226)
//   • Teladoc Health (HealthiestYou)      telehealth — NOT INSURANCE
//
// READ-ONLY. Don't hardcode any of these values elsewhere — import
// from this file. When carrier filings update, update HERE first
// (and notify ops), never patch downstream.
//
// COMPLIANCE: Every indemnity / supplemental product on this list is
// NOT MEC and is not a substitute for major medical insurance. The
// disclaimer renders on every quote that includes one — see
// COMPLIANCE_FLAGS at the bottom.

// ──────────────────────────────────────────────────────────────────
// PRODUCTS — carrier-accurate, sourced from the brochures
// ──────────────────────────────────────────────────────────────────

export const FTPIG_PRODUCTS = [
  // ── Short Term Medical (STM) ─────────────────────────────────────
  // Cert GRI-STAG20-C-E-D + state vars. 10-state footprint.
  // PA = 1-3 mo + 1-mo extension. TX = 3+1.
  { id: 'stm', name: 'Short Term Medical (STM)',
    carrier: 'UHC / Golden Rule', category: 'major_medical_temp',
    underwriting: ['HW','SI'],
    issue_age: [19, 64], hw_chart: 'short_term_medical',
    plans: ['Copay', 'Premier Elite', 'Plus Elite', 'Plus', 'Value'],
    state_pool: ['FL','IN','MI','MS','NE','PA','TN','TX','WI','WV'],
    notes: 'Medically underwritten. Preexisting conditions NOT covered (24-month lookback). Duration: 12 months minus 1 day (PA: 3+1, TX: 3+1). Not MEC.' },

  // ── AccidentWise (GI accident only) ──────────────────────────────
  // Per policy AI-GRI. 42-state footprint. Primary 18-75.
  { id: 'accidentwise', name: 'AccidentWise',
    carrier: 'UHC / Golden Rule', category: 'accident',
    underwriting: ['GI'],
    issue_age: [18, 75],
    plans: ['10000', '15000', '20000'],
    state_pool: ['AK','AL','AR','AZ','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NE','NH','NV','OH','OK','PA','RI','SC','SD','TN','TX','UT','VA','WI','WV','WY'],
    notes: 'GI · accidental injury only. Spouse 16-75, eligible children 0-25. Plans 10K / 15K / 20K. Includes accidental loss of life/limb/sight.' },

  // ── Accident ExpenseGuard ────────────────────────────────────────
  { id: 'expenseguard', name: 'Accident ExpenseGuard',
    carrier: 'UHC / Golden Rule', category: 'accident',
    underwriting: ['GI'],
    issue_age: [18, 64],
    plans: ['$5,000','$7,500','$10,000','$15,000','$20,000'],
    state_pool: ['AL','AK','AZ','AR','CA','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','LA','MD','ME','MI','MN','MO','MS','MT','NC','NE','NV','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VA','WI','WV','WY'],
    notes: '$250 calendar-year deductible (CA: none). AD&D matches accident benefit. Renewable to age 70.' },

  // ── Accident ProGap (CI + AD&D + Hospital Admission) ─────────────
  { id: 'progap', name: 'Accident ProGap',
    carrier: 'UHC / Golden Rule', category: 'accident',
    underwriting: ['GI'],
    issue_age: [18, 64],
    plans: ['$2,500', '$5,000', '$10,000'],
    state_pool: ['AK','AL','AZ','AR','CA','DE','DC','FL','GA','HI','IL','IN','IA','KS','LA','ME','MD','MN','MS','MO','MT','NE','NV','NC','OH','OK','OR','RI','SC','SD','TX','UT','WV','WI','WY'],
    notes: 'Combines accident + critical illness + AD&D + $2,500 hospital admission. 30-day waiting period for CI + hospital admission. Spouse/children CI = 50% of primary.' },

  // ── Accident ProGuard (SI) ───────────────────────────────────────
  { id: 'proguard', name: 'Accident ProGuard',
    carrier: 'UHC / Golden Rule', category: 'accident',
    underwriting: ['SI'],
    issue_age: [18, 64],
    plans: ['$5,000', '$10,000'],
    state_pool: ['AK','AL','AZ','AR','CA','DE','DC','FL','GA','HI','IL','IN','IA','KS','LA','ME','MD','MN','MS','MO','MT','NE','NV','NC','OH','OK','OR','RI','SC','SD','TX','UT','WV','WI','WY'],
    notes: 'Accident + critical illness + AD&D. Simple medical questions. CI lifetime max = 3× chosen amount. CI 30-day waiting period.' },

  // ── Accident ProGuard GI ─────────────────────────────────────────
  { id: 'proguard_gi', name: 'Accident ProGuard GI',
    carrier: 'UHC / Golden Rule', category: 'accident',
    underwriting: ['GI'],
    issue_age: [18, 64],
    plans: ['$5,000', '$7,500'],
    state_pool: ['AK','AL','AZ','AR','CA','DE','DC','FL','GA','HI','IL','IN','IA','KS','LA','ME','MD','MN','MS','MO','MT','NE','NV','NC','OH','OK','OR','RI','SC','SD','TX','UT','WV','WI','WY'],
    notes: 'Guaranteed-issue ProGuard. Smaller benefit caps. No medical questions.' },

  // ── AdvantageGuard (Hospital Indemnity, age 60-90) ───────────────
  { id: 'advantageguard', name: 'AdvantageGuard',
    carrier: 'UHC / Golden Rule', category: 'hospital_indemnity',
    underwriting: ['GI','SI'],
    issue_age: [60, 90],
    plans: ['$50/day – $3,000/day inpatient confinement'],
    state_pool: ['AK','AL','AR','AZ','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NE','NH','NV','OH','OK','PA','RI','SC','SD','TN','TX','UT','VA','WI','WV','WY'],
    notes: 'Ages 60-90 ONLY. Lower benefits = GI; higher = SI. Optional riders: Cancer / Outpatient Rx / ER+UC / SNF / Outpatient Surgical / Major Diagnostic / Ambulance / Wellness. 6-mo preex lookback.' },

  // ── CriticalGuard family ─────────────────────────────────────────
  { id: 'criticalguard_cancer', name: 'CriticalGuard — Cancer',
    carrier: 'UHC / Golden Rule', category: 'critical_illness',
    underwriting: ['GI','SI'],
    issue_age: [18, 90],
    plans: ['$5,000 – $100,000 lifetime maximum'],
    state_pool: ['AK','AL','AR','AZ','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NE','NH','NV','OH','OK','PA','RI','SC','SD','TN','TX','UT','VA','WI','WV','WY'],
    notes: 'Cancer-only plan. $5K-$10K GI; higher = SI. 30-day waiting period. 12-mo preex lookback. Cancer-in-situ + benign brain tumor + skin cancer pay partial.' },

  { id: 'criticalguard_heart', name: 'CriticalGuard — Heart/Stroke',
    carrier: 'UHC / Golden Rule', category: 'critical_illness',
    underwriting: ['GI','SI'],
    issue_age: [18, 90],
    plans: ['$5,000 – $100,000 lifetime maximum'],
    state_pool: ['AK','AL','AR','AZ','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NE','NH','NV','OH','OK','PA','RI','SC','SD','TN','TX','UT','VA','WI','WV','WY'],
    notes: 'Heart attack + stroke. Heart illness (CABG / stent / ablation / etc.) pays 25%. 30-day waiting period.' },

  { id: 'criticalguard_combo', name: 'CriticalGuard — Cancer + Heart/Stroke',
    carrier: 'UHC / Golden Rule', category: 'critical_illness',
    underwriting: ['GI','SI'],
    issue_age: [18, 90],
    plans: ['$5,000 – $100,000 lifetime maximum per category'],
    state_pool: ['AK','AL','AR','AZ','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NE','NH','NV','OH','OK','PA','RI','SC','SD','TN','TX','UT','VA','WI','WV','WY'],
    notes: 'Combines Cancer + Heart/Stroke at the chosen benefit max per category.' },

  { id: 'criticalguard_full', name: 'CriticalGuard — Full Suite',
    carrier: 'UHC / Golden Rule', category: 'critical_illness',
    underwriting: ['GI','SI'],
    issue_age: [18, 90],
    plans: ['$5,000 – $100,000 lifetime maximum per category'],
    state_pool: ['AK','AL','AR','AZ','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NE','NH','NV','OH','OK','PA','RI','SC','SD','TN','TX','UT','VA','WI','WV','WY'],
    notes: 'Adds Advanced Alzheimer\'s, ALS, Coma, End-Stage Renal Failure, Loss of Independent Living (50%), Major Organ Transplant. $10K COVID-ICU benefit included.' },

  // ── Hospital SafeGuard GI ────────────────────────────────────────
  { id: 'hospital_safeguard_gi', name: 'Hospital SafeGuard GI',
    carrier: 'UHC / Golden Rule', category: 'hospital_indemnity',
    underwriting: ['GI'],
    issue_age: [18, 64],
    plans: ['500', '750', '1000', '2000'],
    state_pool: ['AK','AL','AR','AZ','DE','FL','GA','HI','IA','IL','IN','KY','LA','MD','ME','MI','MN','MO','MS','NC','NE','NV','OH','OK','PA','RI','SC','TN','TX','UT','VA','WI','WV','WY'],
    notes: '18-64 (DE + MD: 18-60). GI · no medical underwriting. Includes accidental loss of life/limb/sight rider. 12-mo preex lookback.' },

  // ── HospitalWise ────────────────────────────────────────────────
  { id: 'hospitalwise', name: 'HospitalWise',
    carrier: 'UHC / Golden Rule', category: 'hospital_indemnity',
    underwriting: ['GI'],
    issue_age: [18, 64],
    plans: ['Basic', 'Bundle 1', 'Bundle 2'],
    state_pool: ['AK','AL','AR','AZ','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MD','ME','MI','MN','MO','MS','NC','ND','NE','NV','OH','OK','PA','SC','SD','TN','TX','UT','VA','WI','WV','WY'],
    notes: '18-64. GI. Bundle plans add ER/UC/doctor visit, outpatient surgery, major diagnostic, ambulance, wellness. 6-mo preex lookback.' },

  // ── DentalWise ───────────────────────────────────────────────────
  { id: 'dentalwise', name: 'DentalWise',
    carrier: 'UHC / Golden Rule', category: 'dental_vision',
    underwriting: ['SI'],
    issue_age: [18, 99],
    plans: ['Basic', 'Plan 1000', 'Plan 2000'],
    state_pool: ['AK','AL','AR','AZ','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MD','ME','MI','MN','MO','MS','NC','ND','NE','NH','NV','OH','OK','PA','RI','SC','SD','TN','TX','UT','VA','VT','WI','WV','WY'],
    notes: 'No waiting period for preventive / basic / most major. Implants: 12-mo waiting (Plan 2000 only). Basic NOT available in CO + MN. $100 deductible · $1K-$2K max.' },

  // ── Dental Savings Complete (NOT insurance — discount plan) ─────
  { id: 'dental_savings_complete', name: 'Dental Savings Complete',
    carrier: 'UHC / Golden Rule', category: 'dental_vision',
    underwriting: ['GI'],
    issue_age: [0, 99],
    plans: ['Individual $11.99/mo', 'Family $14.99/mo'],
    state_pool: ['AL','AR','AZ','CA','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MA','MD','ME','MI','MN','MO','MS','NC','NE','NH','NJ','NM','NV','OH','OK','OR','PA','RI','SC','TN','TX','UT','VA','WA','WI','WV'],
    notes: 'NOT INSURANCE — membership discount program. Dental + vision + hearing + Rx via Optum Perks. NOT available in AK, MT, NY, ND, SD, VT, WY. 30-day cancellation.' },

  // ── Teladoc Health (HealthiestYou) — telehealth, NOT insurance ──
  { id: 'teladoc_health', name: 'Teladoc Health (HealthiestYou)',
    carrier: 'Teladoc (separate co.)', category: 'telemedicine',
    underwriting: ['GI'],
    issue_age: [0, 99],
    plans: ['$0 telehealth (unlimited)'],
    state_pool: ['AL','AK','AR','AZ','CA','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NE','NH','NJ','NM','NV','NY','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VA','VT','WA','WI','WV','WY'],
    notes: 'NOT INSURANCE. 24/7 general medical + mental health + dermatology by phone/video/app. Bundled as optional with several Golden Rule plans. Behavioral health + derm may carry add-on fees.' },
];

export const FTPIG_CATEGORIES = [
  { id: 'major_medical_temp', label: 'Temporary Major Medical', tone: 'sky' },
  { id: 'hospital_indemnity', label: 'Hospital Indemnity',      tone: 'blue' },
  { id: 'accident',           label: 'Accident',                tone: 'orange' },
  { id: 'critical_illness',   label: 'Critical Illness',        tone: 'red' },
  { id: 'fixed_indemnity',    label: 'Fixed Indemnity',         tone: 'amber' },
  { id: 'dental_vision',      label: 'Dental / Vision',         tone: 'teal' },
  { id: 'telemedicine',       label: 'Telemedicine',            tone: 'violet' },
  { id: 'life_ancillary',     label: 'Life (Ancillary)',        tone: 'slate' },
];

// ──────────────────────────────────────────────────────────────────
// UNDERWRITING RULES — sourced from brochures
// ──────────────────────────────────────────────────────────────────
export const UNDERWRITING_RULES = {
  stm_preexisting_lookback_months: 24,
  hospital_indemnity_preex_lookback_months: 12,
  critical_illness_preex_lookback_months: 12,
  notice_of_claim_days: 30,
  primary_min_age_default: 18,
  spouse_age_max: 75,
  child_age_max: 25,
};

// ──────────────────────────────────────────────────────────────────
// HEIGHT/WEIGHT CHARTS — placeholder until actual rate tables are
// loaded from the broker portal. The Eligibility Checker shows a
// 'verify in broker portal' note on every H/W decision.
// ──────────────────────────────────────────────────────────────────
export const HW_SHORT_TERM_MEDICAL = {
  58: { male: null,          female: [88, 175] },
  59: { male: null,          female: [91, 181] },
  60: { male: [105, 210],    female: [94, 187] },
  61: { male: [109, 217],    female: [97, 194] },
  62: { male: [112, 224],    female: [100, 201] },
  63: { male: [116, 231],    female: [104, 208] },
  64: { male: [119, 238],    female: [108, 215] },
  65: { male: [123, 246],    female: [111, 223] },
  66: { male: [127, 253],    female: [115, 230] },
  67: { male: [131, 261],    female: [118, 238] },
  68: { male: [134, 269],    female: [122, 245] },
  69: { male: [138, 276],    female: [126, 253] },
  70: { male: [142, 284],    female: [130, 261] },
  71: { male: [146, 292],    female: [134, 269] },
  72: { male: [150, 300],    female: [138, 277] },
  73: { male: [154, 308],    female: [142, 285] },
  74: { male: [158, 316],    female: [146, 293] },
  75: { male: [162, 324],    female: [150, 301] },
  76: { male: [166, 332],    female: [154, 309] },
};

export const HW_ANCILLARY_MAX = {
  58: 210, 59: 217, 60: 225, 61: 232, 62: 240, 63: 248,
  64: 256, 65: 264, 66: 273, 67: 281, 68: 290, 69: 299,
  70: 308, 71: 317, 72: 327, 73: 336, 74: 346, 75: 356, 76: 366,
};

export const HW_TRITERM_TIERED = {
  60: { optimal_max: 180, standard1_max: 210, standard2_max: 240 },
  62: { optimal_max: 195, standard1_max: 225, standard2_max: 258 },
  64: { optimal_max: 210, standard1_max: 242, standard2_max: 276 },
  66: { optimal_max: 225, standard1_max: 260, standard2_max: 295 },
  68: { optimal_max: 240, standard1_max: 278, standard2_max: 315 },
  70: { optimal_max: 260, standard1_max: 300, standard2_max: 338 },
  72: { optimal_max: 280, standard1_max: 322, standard2_max: 362 },
  74: { optimal_max: 300, standard1_max: 345, standard2_max: 386 },
  76: { optimal_max: 320, standard1_max: 370, standard2_max: 410 },
};

// ──────────────────────────────────────────────────────────────────
// STATE AVAILABILITY — STM is the only product with state-specific
// term overrides. Per-product availability lives in each product's
// state_pool field above.
// ──────────────────────────────────────────────────────────────────
export const STATE_AVAILABILITY = {
  stm_eligible_states: ['FL','IN','MI','MS','NE','PA','TN','TX','WI','WV'],
  stm_term_override: { TX: '3+1', PA: '3+1' },
  excluded_states_stm: ['AK','AL','AR','AZ','CA','CO','CT','DC','DE','GA','HI','IA','ID','IL','KS','KY','LA','MA','MD','ME','MN','MO','MT','NC','ND','NH','NJ','NM','NV','NY','OH','OK','OR','RI','SC','SD','UT','VA','VT','WA','WY'],
  triterm_eligible_states: [],
  excluded_states_triterm: [],
  dental_savings_complete_excluded: ['AK','MT','NY','ND','SD','VT','WY'],
};

// ──────────────────────────────────────────────────────────────────
// CLAIM WINDOWS — from each brochure's Notice of Claim provision
// ──────────────────────────────────────────────────────────────────
export const CLAIM_WINDOWS = {
  accidentwise:          { deductible: 0,   filing_days: 90, notes: 'Notice within 30 days. GI · accident-only.' },
  expenseguard:          { deductible: 250, filing_days: 90, notes: 'Notice within 30 days. CA: no deductible.' },
  progap:                { deductible: 250, filing_days: 90, notes: 'Notice within 30 days. CI + AD&D + $2,500 hospital admission.' },
  proguard:              { deductible: 250, filing_days: 90, notes: 'Notice within 30 days. SI version.' },
  proguard_gi:           { deductible: 250, filing_days: 90, notes: 'Notice within 30 days. GI · smaller caps.' },
  advantageguard:        { deductible: 0,   filing_days: 90, notes: 'Notice within 30 days. Ages 60-90.' },
  criticalguard:         { deductible: 0,   filing_days: 90, notes: 'Notice within 30 days. 30-day waiting period for first diagnosis.' },
  hospital_safeguard_gi: { deductible: 0,   filing_days: 90, notes: 'Notice within 30 days. GI · hospital indemnity.' },
  hospitalwise:          { deductible: 0,   filing_days: 90, notes: 'Notice within 30 days. GI · hospital indemnity.' },
  dentalwise:            { deductible: 100, filing_days: 90, notes: 'Notice within 30 days. No waiting for preventive / basic / most major. Implants: 12-mo waiting (Plan 2000 only).' },
};

// ──────────────────────────────────────────────────────────────────
// COMPLIANCE FLAGS — sourced directly from the brochure disclaimers
// ──────────────────────────────────────────────────────────────────
export const COMPLIANCE_FLAGS = [
  {
    id: 'not_mec',
    severity: 'critical',
    text: 'Indemnity / supplemental plans are NOT Minimum Essential Coverage under the ACA. Disclose to client that this is not a substitute for major medical insurance.',
    applies_to: ['hospital_indemnity','accident','critical_illness','fixed_indemnity'],
  },
  {
    id: 'stm_not_mec',
    severity: 'critical',
    text: 'Short Term Medical is a TEMPORARY limited-duration policy. STM may not cover preexisting conditions, may not cover essential health benefits, has no OOP maximum, and the client does NOT qualify for ACA premium subsidies. Point them at HealthCare.gov for comprehensive options.',
    applies_to: ['major_medical_temp'],
  },
  {
    id: 'stm_preex',
    severity: 'high',
    text: 'STM has a 24-month preexisting condition lookback. Anything diagnosed, treated, or showing symptoms in the prior 24 months is excluded. Confirm in writing before binding.',
    applies_to: ['major_medical_temp'],
  },
  {
    id: 'hy_not_insurance',
    severity: 'high',
    text: 'Teladoc Health / HealthiestYou is a telehealth membership, NOT insurance. Behavioral health + dermatology consultations may carry add-on charges beyond the $0 general-medical fee.',
    applies_to: ['telemedicine'],
  },
  {
    id: 'dental_savings_not_insurance',
    severity: 'high',
    text: 'Dental Savings Complete is NOT insurance — it is a discount membership. 30-day cancellation required. NOT available in AK, MT, NY, ND, SD, VT, WY.',
    applies_to: ['dental_vision'],
  },
  {
    id: 'hi_preex',
    severity: 'high',
    text: 'Hospital indemnity (AdvantageGuard / Hospital SafeGuard GI / HospitalWise) carries a 6-12 month preexisting condition lookback. Confirm condition history before issuing.',
    applies_to: ['hospital_indemnity'],
  },
  {
    id: 'ci_waiting',
    severity: 'high',
    text: 'CriticalGuard carries a 30-day waiting period after the effective date. First diagnosis BEFORE day 31 pays nothing (except MD which has no waiting period). 12-month preex lookback also applies.',
    applies_to: ['critical_illness'],
  },
  {
    id: 'advantageguard_age',
    severity: 'medium',
    text: 'AdvantageGuard is only issued to applicants ages 60-90. Verify age before quoting.',
    applies_to: ['hospital_indemnity'],
  },
  {
    id: 'state_avail_brochure',
    severity: 'medium',
    text: 'State availability varies by product. Always verify state eligibility for the SPECIFIC product chosen before binding — see the product\'s state list in this guide.',
    applies_to: ['major_medical_temp','hospital_indemnity','accident','critical_illness','dental_vision'],
  },
];

// ──────────────────────────────────────────────────────────────────
// ELIGIBILITY CHECKER
// ──────────────────────────────────────────────────────────────────
function chartLookup(chart, height) {
  if (!chart) return null;
  const heights = Object.keys(chart).map(Number).sort((a, b) => a - b);
  let row = null;
  for (const h of heights) {
    if (h <= height) row = chart[h];
    else break;
  }
  return row;
}

function checkHwShortTerm(height, weight, sex) {
  if (!height || !weight) return { pass: false, reasons: ['Height + weight required for STM underwriting.'] };
  const row = chartLookup(HW_SHORT_TERM_MEDICAL, height);
  if (!row) return { pass: false, reasons: ['Height outside STM chart.'] };
  const key = sex === 'M' ? 'male' : 'female';
  const range = row[key];
  if (!range) return { pass: false, reasons: [`No ${key} weight range published at this height (verify in broker portal).`] };
  const [min, max] = range;
  if (weight < min) return { pass: false, reasons: [`Weight ${weight}lb under STM minimum ${min}lb (verify in broker portal).`] };
  if (weight > max) return { pass: false, reasons: [`Weight ${weight}lb over STM maximum ${max}lb (verify in broker portal).`] };
  return { pass: true, reasons: [] };
}

export function checkEligibility({ age, state, height, weight, sex }) {
  const results = [];
  const stateUpper = String(state || '').trim().toUpperCase();

  for (const product of FTPIG_PRODUCTS) {
    const reasons = [];
    const hwTier = null;

    // Age gate
    const [aMin, aMax] = product.issue_age;
    if (age != null && (age < aMin || age > aMax)) {
      reasons.push(`Age ${age} outside issue range ${aMin}-${aMax}.`);
    }

    // State gate — use the product's explicit state_pool
    if (stateUpper && product.state_pool && !product.state_pool.includes(stateUpper)) {
      reasons.push(`${product.name} not available in ${stateUpper}.`);
    }
    // STM term override (TX/PA = 3+1)
    if (product.id === 'stm' && STATE_AVAILABILITY.stm_term_override[stateUpper]) {
      reasons.push(`${stateUpper} STM term limited to ${STATE_AVAILABILITY.stm_term_override[stateUpper]}.`);
    }

    // H/W gate (STM only)
    if (product.underwriting?.includes('HW') && product.hw_chart === 'short_term_medical') {
      const hw = checkHwShortTerm(height, weight, sex || 'M');
      if (!hw.pass) reasons.push(...hw.reasons);
    }

    // Compliance flags for this product's category
    const flags = COMPLIANCE_FLAGS.filter(f => f.applies_to?.includes(product.category));

    results.push({
      product,
      pass: reasons.length === 0,
      reasons,
      hwTier,
      flags,
    });
  }
  return results;
}
